import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { BookingBrandHeader } from "@/components/BookingBrandHeader";
import { PaymentSummary } from "@/components/guest-portal/PaymentSummary";
import { useLocale } from "@/i18n";
import { api, ApiError } from "@/lib/api";
import { formatLocaleDate } from "@/lib/dates";
import { formatCurrency } from "@/lib/money";
import { clearGuestSession, loadGuestSession } from "@/lib/guestAuth";
import { periodLabelBadgeClass } from "@/lib/guestPortalLabels";
import type { BookingSummary } from "@/lib/guestPortalTypes";
import { useCampsiteBranding } from "@/lib/useCampsiteBranding";
import { useTouristIntegrationEnabled } from "@/lib/useTouristIntegrationEnabled";
import { displayBookingNotes } from "@/lib/bookingNotes";
import { companionSlotLabel, type CompanionFieldKey } from "@/lib/companionFields";
import { formatCompanionFieldValue } from "@/lib/guestProfile";

export function GuestBookingDetailPage() {
  const { locale, messages, localePath } = useLocale();
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const { siteName, logoUrl } = useCampsiteBranding();
  const { enabled: touristIntegrationEnabled, loading: touristConfigLoading } =
    useTouristIntegrationEnabled();
  const [detail, setDetail] = useState<BookingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const periodLabelText = (label: string): string =>
    messages.guestPortalVehicle[label as keyof typeof messages.guestPortalVehicle] ?? label;

  const arrivalTypeLabel = (code: string | null | undefined): string => {
    if (!code) return messages.common.dash;
    return (
      messages.guestPortalVehicle[code as keyof typeof messages.guestPortalVehicle] ?? code
    );
  };

  const bookingStatusLabel = (status: string): string =>
    messages.bookingStatus[status as keyof typeof messages.bookingStatus] ?? status;

  const load = useCallback(async () => {
    if (!loadGuestSession()) {
      navigate(localePath("/login"), { replace: true });
      return;
    }
    if (!bookingId) {
      navigate(localePath("/portal"), { replace: true });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<BookingSummary>(
        `/api/v1/public/guest/bookings/${encodeURIComponent(bookingId)}`,
      );
      setDetail(data);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        clearGuestSession();
        navigate(localePath("/login"), { replace: true });
        return;
      }
      setError(e instanceof ApiError ? e.message : messages.bookingDetail.loadError);
    } finally {
      setLoading(false);
    }
  }, [bookingId, navigate, localePath, messages.bookingDetail.loadError]);

  useEffect(() => {
    void load();
  }, [load]);

  const booking = detail?.booking;
  const bookingMessage = displayBookingNotes(booking?.notes);

  if (!touristConfigLoading && touristIntegrationEnabled) {
    return <Navigate to={localePath("/buchen")} replace />;
  }

  return (
    <div className="mx-auto min-h-screen max-w-2xl px-4 py-8 pb-12">
      <BookingBrandHeader
        siteName={siteName}
        logoUrl={logoUrl}
        title={
          booking && booking.status === "requested"
            ? bookingStatusLabel(booking.status)
            : (booking?.booking_number ?? messages.fields.travel_data)
        }
        subtitle={
          booking
            ? `${formatLocaleDate(booking.arrival_date, locale)} – ${formatLocaleDate(booking.departure_date, locale)}`
            : undefined
        }
      />

      <p className="mb-6">
        <Link to={localePath("/portal")} className="text-sm font-medium text-brand-700 hover:underline">
          {messages.bookingDetail.backToPortal}
        </Link>
      </p>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="text-center text-sm text-slate-500">{messages.common.loading}</p>
      ) : detail && booking ? (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-2">
            <span
              className={`rounded-full px-3 py-1 text-xs font-medium ${periodLabelBadgeClass(detail.period_label)}`}
            >
              {periodLabelText(detail.period_label)}
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">
              {bookingStatusLabel(booking.status)}
            </span>
          </div>

          <section className="bk-card space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">{messages.fields.travel_data}</h2>
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-slate-500">{messages.fields.arrival}</dt>
                <dd className="font-medium">{formatLocaleDate(booking.arrival_date, locale)}</dd>
              </div>
              <div>
                <dt className="text-slate-500">{messages.fields.departure}</dt>
                <dd className="font-medium">{formatLocaleDate(booking.departure_date, locale)}</dd>
              </div>
              <div>
                <dt className="text-slate-500">{messages.fields.nights}</dt>
                <dd className="font-medium">{booking.nights}</dd>
              </div>
              <div>
                <dt className="text-slate-500">{messages.fields.travelers}</dt>
                <dd className="font-medium">
                  {booking.adults} {messages.common.adults}
                  {booking.children > 0 ? `, ${booking.children} ${messages.common.children}` : ""}
                  {booking.dogs > 0 ? `, ${booking.dogs} ${messages.common.dogs}` : ""}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">{messages.fields.arrival_type}</dt>
                <dd className="font-medium">{arrivalTypeLabel(booking.arrival_type)}</dd>
              </div>
              <div>
                <dt className="text-slate-500">{messages.fields.electricity}</dt>
                <dd className="font-medium">
                  {booking.wants_electricity
                    ? messages.guestPortalVehicle.electricity_yes
                    : messages.guestPortalVehicle.electricity_no}
                </dd>
              </div>
              {booking.license_plate ? (
                <div className="sm:col-span-2">
                  <dt className="text-slate-500">{messages.fields.license_plate}</dt>
                  <dd className="font-medium">
                    {booking.license_plate}
                    {booking.license_plate_country
                      ? ` (${booking.license_plate_country})`
                      : ""}
                  </dd>
                </div>
              ) : null}
            </dl>
          </section>

          <section className="bk-card">
            <h2 className="mb-3 text-lg font-semibold text-slate-900">{messages.fields.price}</h2>
            <p className="text-2xl font-bold text-brand-800">
              {booking.price_pending ? "Preis folgt" : formatCurrency(booking.total_amount, booking.currency)}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              {booking.price_pending
                ? "Der Preis für diesen Zeitraum wird noch mitgeteilt."
                : messages.common.inclVat}
            </p>
          </section>

          <PaymentSummary
            payment={detail.payment}
            totalAmount={booking.total_amount}
            currency={booking.currency}
          />

          {bookingMessage ? (
            <section className="bk-card">
              <h2 className="text-lg font-semibold text-slate-900">{messages.fields.your_message}</h2>
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{bookingMessage}</p>
            </section>
          ) : null}

          {booking.companions && booking.companions.length > 0 ? (
            <section className="bk-card">
              <h2 className="text-lg font-semibold text-slate-900">{messages.flow.companionsSummary}</h2>
              <ul className="mt-3 space-y-3 text-sm text-slate-700">
                {(() => {
                  let adultIndex = 0;
                  let childIndex = 0;
                  return booking.companions.map((c, i) => {
                    const row = c as Record<string, string | undefined>;
                    const slotIndex = row.role === "child" ? childIndex++ : adultIndex++;
                    const title = companionSlotLabel(
                      row.role === "child" ? "child" : "adult",
                      slotIndex,
                    );
                    const fields = Object.entries(row).filter(
                      ([key, value]) =>
                        key !== "role" &&
                        key in messages.companionFields &&
                        typeof value === "string" &&
                        value.trim(),
                    );
                    return (
                      <li key={i} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                        <p className="font-semibold text-slate-800">{title}</p>
                        {fields.length > 0 ? (
                          <dl className="mt-1 space-y-0.5">
                            {fields.map(([key, value]) => (
                              <div key={key} className="flex gap-2">
                                <dt className="text-slate-500">
                                  {messages.companionFields[key as keyof typeof messages.companionFields]}:
                                </dt>
                                <dd className="font-medium text-slate-900">
                                  {formatCompanionFieldValue(
                                    key as CompanionFieldKey,
                                    value!,
                                  )}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        ) : null}
                      </li>
                    );
                  });
                })()}
              </ul>
            </section>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
